// CreditLedger v2 — Per-Lead-Per-Phase Credit-Modell (Welle Pricing-v2).
//
// Source-of-truth bleibt creditEvents/{eventId} (siehe creditEvent.ts).
// Diese Datei addiert:
//   - CreditTransaction: convenience-Shape für Frontend/Reporting der jede
//     Phase-Buchung explizit mit phase + leadId taggt
//   - CreditPricing: Konstanten (was kostet welche Phase) — single source of
//     truth für Backend-Deduktion + UI-Anzeige
//
// Bewusst hier separiert von creditEvent.ts weil:
//   1. creditEvent.ts ist Stripe/Billing-Domain (grants/clawbacks/refunds)
//   2. creditLedger.ts ist Cortex-Pipeline-Domain (per-phase lead consumption)
//   3. Die zwei werden nie zusammen mutiert — Stripe-Webhook touched A, der
//      Scraper touched B. Gemeinsamer Output-Ledger sind `creditEvents/`.
//
// Siehe docs/credit-model-v2.md für Doktrin, Migration und Edge-Cases.

// ---------------------------------------------------------------------------
// Phase-Identifier
// ---------------------------------------------------------------------------
//
// 'identify' | 'connect' | 'pitch' folgen der Naming-Convention in
// docs/parallel-work/interfaces.md (Phase 1 = Company-Identification,
// Phase 2 = Person-Research/Connect, Phase 3 = Enrichment/Pitch).
//
// 'legacy' ist für alle Lead-Deliveries VOR Tag X (Hard-Switch). Diese
// haben alle 3 Phasen für 1 Credit bekommen — kein Phase-Tag möglich.

export type CreditPhase = 'identify' | 'connect' | 'pitch' | 'legacy';

// ---------------------------------------------------------------------------
// CreditTransaction
// ---------------------------------------------------------------------------
//
// Eine Buchung im Per-Phase-Modell. Wird beim Live-Schalten der Endpoints
// (Welle F2.2/F2.3) als zusätzlicher Tag auf das underlying CreditEvent
// geschrieben — also CreditEvent { reason: 'phase_connect', leadId, phase: 'connect' }.
//
// Für reine Phase-1-Identify-Buchungen tagged der Orchestrator beim Switchover
// die existing 'lead_delivered'-Events mit phase: 'identify'.
//
// Backend-Mapping (geplant für Welle F2.2):
//   POST /leads/:id/connect/start  → CreditTransaction { phase: 'connect', credits: -1, leadId }
//   POST /leads/:id/pitch/start    → CreditTransaction { phase: 'pitch',   credits: -1, leadId }
//   Lead-Delivery in Orchestrator → CreditTransaction { phase: 'identify', credits: -1, leadId }

export type CreditTransaction = {
  /** UID des Users, dem der Credit abgebucht wird */
  userId: string;
  /** Lead-ID — bei 'identify'/'connect'/'pitch' Pflicht, bei 'legacy' optional */
  leadId?: string;
  /** Welche Phase löst die Buchung aus */
  phase: CreditPhase;
  /** Credit-Delta. NEGATIV bei Konsum (z.B. -1), positiv bei (manuellem) Refund */
  credits: number;
  /** Unix-ms */
  ts: number;
  /** Free-text Buchungsgrund. Im UI eingeblendet im Credit-History-Drawer */
  reason: string;
  /** Optional: Run-ID falls Buchung Teil eines Scraper-Runs */
  runId?: string;
  /** Optional: ID des zugrunde liegenden creditEvents/{eventId} für Cross-Reference */
  creditEventId?: string;
};

// ---------------------------------------------------------------------------
// CreditPricing
// ---------------------------------------------------------------------------
//
// Konstante Pricing-Tabelle. Im v2.0 sind alle 3 Phasen 1 Credit; die
// Konstanten sind hier damit eine spätere Differenzierung (z.B. Phase 2
// auf 2 Credits ziehen) eine 1-Zeilen-Änderung wird, ohne dass Backend
// + Frontend separat patchen müssen.
//
// `currency: 'EUR'` ist informational — der tatsächliche EUR-Wert pro Credit
// kommt aus SUBSCRIPTION_TIERS / CREDIT_PACKS (siehe billing.ts).

export type CreditPricing = {
  /** Credits abgebucht beim Identifizieren eines Leads (Phase 1 / Run-Start) */
  identifyCost: number;
  /** Credits abgebucht beim User-Klick "Ansprechpartner finden" (Phase 2) */
  connectCost: number;
  /** Credits abgebucht beim User-Klick "Voll-Insights" (Phase 3) */
  pitchCost: number;
  /** Informational — Credit-Wert in EUR siehe billing.ts SUBSCRIPTION_TIERS */
  currency: 'EUR' | 'USD';
  /**
   * Optional: Bundle-Discounts. v2.0 hat KEINE — bewusst leer um Komplexität
   * draußen zu halten. Welle Pricing-v2.3 kann hier z.B. einen
   * "all-leads-phase-3"-Bundle hinzufügen.
   */
  bundleDiscounts?: Array<{ name: string; discount: number; conditions: string }>;
};

/**
 * DEFAULT_CREDIT_PRICING — v2.0 Pricing-Konstanten.
 *
 * Single source of truth. Backend importiert für Credit-Deduktion,
 * Frontend importiert für Button-Labels ("+ Ansprechpartner finden (1 Credit)").
 *
 * Änderungen hier sind ein BREAKING-EVENT für Customer-Kommunikation — siehe
 * docs/credit-model-v2.md Sektion 6 (Email-Template) bevor du den Cost erhöhst.
 */
export const DEFAULT_CREDIT_PRICING: CreditPricing = {
  identifyCost: 1,
  connectCost: 1,
  pitchCost: 1,
  currency: 'EUR',
};

/**
 * Helper: was kostet eine bestimmte Phase laut Pricing-Tabelle.
 * Frontend nutzt das in PhaseButtons.tsx für die Confirm-Dialog-Anzeige.
 * `'legacy'` returnt 0 weil legacy-Leads bereits am Run-Start bezahlt wurden.
 */
export function getPhaseCost(
  phase: CreditPhase,
  pricing: CreditPricing = DEFAULT_CREDIT_PRICING,
): number {
  switch (phase) {
    case 'identify':
      return pricing.identifyCost;
    case 'connect':
      return pricing.connectCost;
    case 'pitch':
      return pricing.pitchCost;
    case 'legacy':
      return 0;
  }
}
